/**
 * Formatting helpers shared by the dashboard widgets.
 * Pure functions — no Vue/i18n coupling, so they stay unit-testable.
 */

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const

/** Human-readable byte size, e.g. `1.2 GB`. */
export function formatBytes(bytes: number, digits = 1): string {
  const n = Number.isFinite(bytes) ? Math.max(0, bytes) : 0
  if (n < 1) return '0 B'
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), BYTE_UNITS.length - 1)
  const value = n / 1024 ** i
  const d = i === 0 ? 0 : digits
  return `${value.toFixed(d)} ${BYTE_UNITS[i]}`
}

/** Human-readable transfer rate, e.g. `12.3 MB/s`. */
export function formatSpeed(bytesPerSec: number, digits = 1): string {
  return `${formatBytes(bytesPerSec, digits)}/s`
}

/** Bytes → megabytes (chart axis unit). */
export function bytesToMB(bytes: number): number {
  const n = Number.isFinite(bytes) ? Math.max(0, bytes) : 0
  return n / (1024 * 1024)
}

/**
 * Relative time such as `2 分钟前`. `locale` selects the wording; anything other
 * than `zh-CN` uses English.
 */
export function formatRelativeTime(ts: number, locale = 'zh-CN', now = Date.now()): string {
  if (!Number.isFinite(ts) || ts <= 0) return locale === 'zh-CN' ? '未知' : 'unknown'
  // tolerate seconds-based timestamps
  const ms = ts < 1e12 ? ts * 1000 : ts
  const diff = Math.max(0, now - ms)
  const sec = Math.floor(diff / 1000)
  const zh = locale === 'zh-CN'

  if (sec < 10) return zh ? '刚刚' : 'just now'
  if (sec < 60) return zh ? `${sec} 秒前` : `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return zh ? `${min} 分钟前` : `${min}m ago`
  const hour = Math.floor(min / 60)
  if (hour < 24) return zh ? `${hour} 小时前` : `${hour}h ago`
  const day = Math.floor(hour / 24)
  if (day < 30) return zh ? `${day} 天前` : `${day}d ago`
  const month = Math.floor(day / 30)
  if (month < 12) return zh ? `${month} 个月前` : `${month}mo ago`
  return zh ? `${Math.floor(month / 12)} 年前` : `${Math.floor(month / 12)}y ago`
}

/** `mm:ss` clock label for the chart x-axis. */
export function formatClock(ts: number): string {
  const d = new Date(ts)
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${mm}:${ss}`
}
